import { gql, useMutation } from '@apollo/client';
import type { Transfer } from '@interfaces/transfer';
import { Link as MuiLink } from '@ovComponents/1-primative/link/link';
import { Badge } from '@ovComponents/2-component/badge/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { TransferModal } from '@ovComponents/4-module/widgets/pendingTransfers/components/transferModal';
import { useGlobalToast } from '@providers/globalToastProvider';
import { usePermissions } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useLocalization } from '@utils/useLocalization';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const TRANSITION_TRANSFER = gql`
  mutation transitionTransfer($input: TransitionTransferInput!) {
    transitionTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const PendingTransactionTable = ({
  transfers,
  refetch,
}: {
  transfers: Transfer[];
  refetch: () => void;
}) => {
  const { t } = useTranslation(['components', 'shared', 'transfer']);
  const { showToast } = useGlobalToast();
  const { localizedDateTime } = useLocalization();

  const { permissions } = usePermissions();

  const [activeTransfer, setActiveTransfer] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);

  const [transitionTransfer] = useMutation(TRANSITION_TRANSFER, {
    onError: (error: any) => {
      showToast({
        message:
          error?.graphQLErrors[0]?.extensions?.response?.body?.errors[0]
            ?.message || '',
        severity: 'error',
      });
    },
  });

  const stateColor = (forState: any) =>
    forState === 'RECONCILED'
      ? 'positive'
      : ['FAILED', 'CANCELED'].includes(forState)
        ? 'negative'
        : 'warning';

  const sourceDetails = (transfer: any): string => {
    if (transfer.dividendFor) {
      return ` - ${transfer.dividendFor.ticker}`;
    }
    if (transfer.bankAccount) {
      return ` - ${transfer.bankAccount.institutionNumber}`;
    }
    return '';
  };

  const generateSourceText = (transfer: any): string => {
    if (['TO_ACCOUNT', 'TO_SUB_ACCOUNT'].includes(transfer.source as string)) {
      const accType = t(
        `accountTypes:${transfer?.fromSubAccount?.account?.type}`,
      );
      return t('transferTo', { subAccount: accType });
    }
    if (
      ['FROM_ACCOUNT', 'FROM_SUB_ACCOUNT'].includes(transfer.source as string)
    ) {
      const accType = t(
        `accountTypes:${transfer?.fromSubAccount?.account?.type}`,
      );
      return t('transferFrom', { subAccount: accType });
    }
    const sourceOption = transfer.initiatedByRepresentative
      ? 'REPRESENTATIVE'
      : transfer.source;
    return t(`transferSources.${sourceOption}`);
  };

  const navigationLink = (user: any): string => {
    if (user.type === 'INDIVIDUAL') return `/clients/${user.id}`;
    return `/nonIndividualClients/${user.id}`;
  };

  const transition = (
    transferId: string,
    _isScheduled: boolean,
    variables: any,
  ) => {
    transitionTransfer({
      variables: { input: { transferId, ...variables } },
      onCompleted: refetch,
    });
  };

  return (
    <>
      <Table aria-label='table'>
        <TableBody>
          <TableRow>
            <TableHeadCell>{t('activityTable.client')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.account')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.type')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.source')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.amount')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.state')}</TableHeadCell>
            <TableHeadCell>{t('activityTable.createdAt')}</TableHeadCell>
          </TableRow>
          {transfers.map((transfer: any) => (
            <TableRow
              hover
              pointer
              onClick={() => {
                setActiveTransfer(transfer);
                setModalOpen(true);
              }}
              selected={transfer.id === activeTransfer.id}
              key={transfer.id}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                textDecoration: 'none',
              }}
            >
              <TableCell component='th' scope='row'>
                <MuiLink
                  component={Link}
                  to={navigationLink(transfer?.subAccount?.account?.user)}
                >
                  {generateClientNameString(
                    transfer?.subAccount?.account?.user,
                  )}
                </MuiLink>
              </TableCell>
              <TableCell component='th' scope='row'>
                {transfer?.subAccount?.goal?.name} -{' '}
                {t(`accountTypes:${transfer?.subAccount?.account?.type}`)}
              </TableCell>
              <TableCell>
                <Badge
                  label={
                    transfer?.isWithdrawAll
                      ? t('components:transferModal.withdrawAll')
                      : t(`transfer:types.${transfer?.type}`)
                  }
                />
              </TableCell>
              <TableCell>
                {generateSourceText(transfer)}
                <b>{sourceDetails(transfer)}</b>
              </TableCell>
              <TableCell>{formatMoneyValue(transfer?.amountCents)}</TableCell>
              <TableCell>
                <Badge
                  label={t(`transfer:states.${transfer?.state}`)}
                  color={stateColor(transfer?.state)}
                />
              </TableCell>
              <TableCell>{localizedDateTime(transfer?.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {activeTransfer && (
        <TransferModal
          transfer={activeTransfer}
          handleClose={() => setModalOpen(false)}
          open={modalOpen}
          transition={transition}
          options={{
            canTransition:
              permissions.includes('transition:transfer') ||
              permissions.includes('write:adjustment_transfer'),
          }}
        />
      )}
    </>
  );
};
